import { MathWalletAdapter } from '@solana/wallet-adapter-mathwallet';
import { PhantomWalletAdapter } from '@solana/wallet-adapter-phantom';
import {
  ConnectionContext,
  WalletProvider,
} from '@solana/wallet-adapter-react';
import { WalletModalProvider } from '@solana/wallet-adapter-react-ui';
import { SolflareWalletAdapter } from '@solana/wallet-adapter-solflare';
import { useCallback, useMemo } from 'react';
import { useTRPC } from '../hooks/useTRPC';
import { TRPC_URL } from '../lib/config';
import {
  BackendProxyConnection,
  resolveProxyEndpoint,
} from '../utils/BackendProxyConnection';

import '@solana/wallet-adapter-react-ui/styles.css';
import '../styles/wallet-adapter-modal.css';

interface WalletContextProviderProps {
  children: React.ReactNode;
}

export function WalletContextProvider({
  children,
}: WalletContextProviderProps) {
  const trpc = useTRPC();

  const proxyEndpoint = useMemo(() => resolveProxyEndpoint(TRPC_URL), []);

  const proxyRequest = useCallback(
    (request: {
      jsonrpc: '2.0';
      id: string;
      method: string;
      params: unknown[];
    }) => trpc.solanaRpc.proxy.mutate(request),
    [trpc],
  );

  const connection = useMemo(
    () =>
      new BackendProxyConnection(proxyEndpoint, proxyRequest, {
        commitment: 'confirmed',
        disableRetryOnRateLimit: true,
      }),
    [proxyEndpoint, proxyRequest],
  );

  const wallets = useMemo(
    () => [
      new PhantomWalletAdapter(),
      new SolflareWalletAdapter(),
      new MathWalletAdapter(),
    ],
    [],
  );

  const connectionContextValue = useMemo(() => ({ connection }), [connection]);

  return (
    <ConnectionContext.Provider value={connectionContextValue}>
      <WalletProvider wallets={wallets} autoConnect>
        <WalletModalProvider>{children}</WalletModalProvider>
      </WalletProvider>
    </ConnectionContext.Provider>
  );
}
